import { useEffect, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input, InputGroup, InputGroupText, FormFeedback,
    Button, Spinner, Row, Col, Alert,
} from 'reactstrap';
import { BiDownload, BiErrorCircle, BiCopyAlt, BiLockAlt } from 'react-icons/bi';
import { toast } from 'react-toastify';
import useAttachmentsByInvoice from '../../hooks/useAttachmentsByInvoice';
import useUpdateInvoice from '../../hooks/useUpdateInvoice';
import useCategories, { CATEGORY_TARGET } from '../../hooks/useCategories';
import { fetchInvoiceAttachmentDownloadUrl, checkInvoiceDuplicates, dismissInvoiceDuplicate } from '../../api/invoicesApi';
import AccountingSyncBadge from '../integrations/AccountingSyncBadge';
import DuplicateWarning from '../common/DuplicateWarning';
import { currencySymbol, CURRENCIES } from '../../constants/currencies';

// Revenue-side twin of expenseDetailModal. Same two-pane layout — left pane
// shows the attachment preview, right pane holds the editable form. Save
// clears OcrFieldConfidence server-side so the "Please check" pill drops.

const CONFIDENCE_FLOOR = 0.85;
const isLowConf = (conf, key) =>
    conf != null && conf[key] != null && conf[key] < CONFIDENCE_FLOOR;

function validateInvoice({ amount, vatAmount, invoiceDate }) {
    const errors = {};
    if (!invoiceDate)                    errors.invoiceDate = 'Invoice date is required.';
    if (amount === '' || amount == null) errors.amount      = 'Amount is required.';
    else if (Number.isNaN(Number(amount)) || Number(amount) < 0)
                                         errors.amount      = 'Amount must be zero or positive.';
    if (vatAmount !== '' && vatAmount != null) {
        if (Number.isNaN(Number(vatAmount)) || Number(vatAmount) < 0)
            errors.vatAmount = 'VAT amount must be zero or positive.';
    }
    return { errors, hasErrors: Object.keys(errors).length > 0 };
}

const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

export default function InvoiceDetailModal({ isOpen, onClose, invoice, currency = 'GBP' }) {
    const updateInvoice = useUpdateInvoice();
    const attachments = useAttachmentsByInvoice(isOpen ? invoice?.id : null);
    const attachment = attachments.data?.[0] ?? null;
    const categories = useCategories(CATEGORY_TARGET.Invoice);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const [invoiceDate, setInvoiceDate] = useState(todayLocal());
    const [customer, setCustomer]       = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory]       = useState('');
    const [amount, setAmount]           = useState('');
    const [vatAmount, setVatAmount]     = useState('');
    const [currencyValue, setCurrencyValue] = useState(currency);
    const [attempted, setAttempted]     = useState(false);
    const [dupMatches, setDupMatches]   = useState([]);
    const [dupChecking, setDupChecking] = useState(false);
    const [dupFlag, setDupFlag]         = useState(false);
    const [dismissing, setDismissing]   = useState(false);
    const [locked, setLocked]           = useState(false);

    const { errors, hasErrors } = validateInvoice({ amount, vatAmount, invoiceDate });

    useEffect(() => {
        if (!isOpen || !invoice) return;
        setInvoiceDate(invoice.invoiceDate ?? todayLocal());
        setCustomer(invoice.customer ?? '');
        setDescription(invoice.description ?? '');
        setCategory(invoice.categoryId != null ? String(invoice.categoryId) : '');
        setAmount(invoice.amount != null ? Number(invoice.amount).toFixed(2) : '');
        setVatAmount(invoice.vatAmount != null ? Number(invoice.vatAmount).toFixed(2) : '');
        setCurrencyValue(invoice.currency ?? currency);
        setAttempted(false);
        setDupMatches([]);
        setDupChecking(false);
        setDupFlag(Boolean(invoice.possibleDuplicate));
        setLocked(false);
    }, [isOpen, invoice]);

    const handleDismissDuplicate = async () => {
        if (!invoice?.id) return;
        setDismissing(true);
        try {
            await dismissInvoiceDuplicate(invoice.id);
            setDupFlag(false);
            toast.success('Duplicate flag cleared');
        } catch (err) {
            toast.error(err?.message ?? 'Failed to clear duplicate flag');
        } finally {
            setDismissing(false);
        }
    };

    useEffect(() => {
        if (!isOpen || !attachment?.id) {
            setPreviewUrl(null);
            setPreviewError(null);
            return;
        }
        let cancelled = false;
        setPreviewLoading(true);
        setPreviewError(null);
        fetchInvoiceAttachmentDownloadUrl(attachment.id, { inline: true })
            .then(({ url }) => { if (!cancelled) setPreviewUrl(url); })
            .catch(err => { if (!cancelled) setPreviewError(err?.message ?? 'Failed to load preview'); })
            .finally(() => { if (!cancelled) setPreviewLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, attachment?.id]);

    const handleClose = () => {
        if (updateInvoice.isPending) return;
        onClose();
    };

    const handleDownload = async () => {
        if (!attachment) return;
        try {
            const { url } = await fetchInvoiceAttachmentDownloadUrl(attachment.id);
            window.location.href = url;
        } catch (err) {
            toast.error(err?.message ?? 'Failed to prepare download');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (locked || updateInvoice.isPending || dupChecking) return;
        if (hasErrors) { setAttempted(true); return; }

        setDupChecking(true);
        let matches = [];
        try {
            matches = await checkInvoiceDuplicates({
                amount: Number(amount),
                date: invoiceDate,
                customer: customer.trim() || undefined,
                excludeId: invoice.id,
            });
        } catch { /* ignore — warning is advisory */ }
        setDupChecking(false);
        if (matches.length > 0) { setDupMatches(matches); return; }

        await doSave();
    };

    const doSave = async () => {
        if (updateInvoice.isPending) return;
        await updateInvoice.mutateAsync({
            id: invoice.id,
            body: {
                invoiceDate,
                customer:    customer.trim()    || null,
                description: description.trim() || null,
                categoryId:  category === '' ? null : Number(category),
                amount:      Number(amount),
                vatAmount:   vatAmount === '' ? null : Number(vatAmount),
                currency:    currencyValue,
            },
        });
        onClose();
    };

    const ocrConf = invoice?.ocrFieldConfidence;
    const lowConfFeedback = (key) =>
        isLowConf(ocrConf, key)
            ? <div className="text-warning small mt-1 d-inline-flex align-items-center gap-1">
                  <BiErrorCircle /> Detected with low confidence — please verify
              </div>
            : null;

    const isImage = attachment?.contentType?.startsWith('image/');
    const isPdf   = attachment?.contentType === 'application/pdf';

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered backdrop="static" size="xl">
            <ModalHeader toggle={handleClose}>
                Invoice details
                {attachment && (
                    <Button color="link" size="sm" className="ms-3 p-0 text-decoration-none"
                            onClick={handleDownload}
                            title="Download original">
                        <BiDownload className="me-1" /> Download
                    </Button>
                )}
            </ModalHeader>
            <Form onSubmit={handleSubmit} noValidate>
                <ModalBody>
                    <Row className="g-3">
                        {/* ---- Left: attachment preview ---- */}
                        <Col md={7}>
                            <div className="border rounded bg-light d-flex align-items-center justify-content-center"
                                 style={{ minHeight: 480, height: '70vh', overflow: 'hidden' }}>
                                {attachments.isLoading ? (
                                    <Spinner />
                                ) : attachments.error ? (
                                    <div className="text-danger small p-3 text-center">
                                        Failed to load attachment.
                                    </div>
                                ) : !attachment ? (
                                    <div className="text-muted small p-3 text-center">
                                        No attachment for this invoice.
                                    </div>
                                ) : previewLoading ? (
                                    <Spinner />
                                ) : previewError ? (
                                    <div className="text-danger small p-3 text-center">
                                        {previewError}
                                    </div>
                                ) : previewUrl && isImage ? (
                                    <img src={previewUrl}
                                         alt={attachment.originalFileName}
                                         style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                ) : previewUrl && isPdf ? (
                                    <iframe src={`${previewUrl}#view=FitH&navpanes=0`}
                                            title={attachment.originalFileName}
                                            className="w-100 h-100 border-0" />
                                ) : (
                                    <div className="text-muted small p-3 text-center">
                                        Preview not available for this file type.
                                        <div className="mt-2">
                                            <Button color="primary" size="sm" outline onClick={handleDownload}>
                                                <BiDownload className="me-1" /> Download instead
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {attachment && (
                                <div className="text-muted small mt-2 text-truncate">
                                    {attachment.originalFileName}
                                </div>
                            )}
                        </Col>

                        {/* ---- Right: editable form ---- */}
                        <Col md={5}>
                            {locked && (
                                <Alert color="secondary" className="d-flex align-items-start gap-2 py-2 small">
                                    <BiLockAlt className="mt-1 flex-shrink-0" size={18} />
                                    <div>This invoice is paid and locked in your accounting package, so it can’t be edited or deleted here.</div>
                                </Alert>
                            )}
                            <fieldset disabled={locked} className="border-0 p-0 m-0">
                            {dupFlag && (
                                <Alert color="warning" className="d-flex align-items-start gap-2 py-2 small">
                                    <BiCopyAlt className="mt-1 flex-shrink-0" size={18} />
                                    <div className="flex-grow-1">
                                        This was flagged as a possible duplicate of an existing invoice.
                                        Saving any edit clears this, or:
                                        <div className="mt-2">
                                            <Button color="warning" size="sm" outline
                                                    disabled={dismissing} onClick={handleDismissDuplicate}>
                                                {dismissing && <Spinner size="sm" className="me-1" />}
                                                Not a duplicate
                                            </Button>
                                        </div>
                                    </div>
                                </Alert>
                            )}
                            <DuplicateWarning matches={dupMatches} currency={currencyValue} />
                            {ocrConf && Object.values(ocrConf).some(v => v < CONFIDENCE_FLOOR) && (
                                <Alert color="warning" className="d-flex align-items-start gap-2 py-2 small">
                                    <BiErrorCircle className="mt-1 flex-shrink-0" />
                                    <div>
                                        Some fields were detected with low confidence. Please verify and save.
                                    </div>
                                </Alert>
                            )}
                            <Row>
                                <Col xs={6}>
                                    <FormGroup>
                                        <Label>Invoice Date *</Label>
                                        <Input
                                            type="date"
                                            value={invoiceDate}
                                            onChange={(e) => setInvoiceDate(e.target.value)}
                                            invalid={attempted && !!errors.invoiceDate}
                                        />
                                        <FormFeedback>{errors.invoiceDate}</FormFeedback>
                                        {lowConfFeedback('invoiceDate')}
                                    </FormGroup>
                                </Col>
                                <Col xs={6}>
                                    <FormGroup>
                                        <Label>Category</Label>
                                        <Input
                                            type="select"
                                            value={category}
                                            onChange={(e) => setCategory(e.target.value)}
                                            disabled={categories.isLoading}
                                            className={isLowConf(ocrConf, 'category') ? 'border-warning' : ''}
                                        >
                                            <option value="">— Select category —</option>
                                            {(categories.data ?? []).map((c) => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </Input>
                                        {lowConfFeedback('category')}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup>
                                <Label>Customer</Label>
                                <Input value={customer} onChange={(e) => setCustomer(e.target.value)} />
                                {lowConfFeedback('customer')}
                            </FormGroup>
                            <FormGroup>
                                <Label>Description</Label>
                                <Input
                                    type="textarea"
                                    rows={2}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Currency</Label>
                                <Input
                                    type="select"
                                    value={currencyValue}
                                    onChange={(e) => setCurrencyValue(e.target.value)}
                                    className={isLowConf(ocrConf, 'currency') ? 'border-warning' : ''}
                                >
                                    {CURRENCIES.map((c) => (
                                        <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>
                                    ))}
                                </Input>
                                {lowConfFeedback('currency')}
                            </FormGroup>
                            <Row>
                                <Col xs={6}>
                                    <FormGroup>
                                        <Label>Amount *</Label>
                                        <InputGroup>
                                            <InputGroupText>{currencySymbol(currencyValue)}</InputGroupText>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                invalid={attempted && !!errors.amount}
                                            />
                                            <FormFeedback>{errors.amount}</FormFeedback>
                                        </InputGroup>
                                        {lowConfFeedback('amount')}
                                    </FormGroup>
                                </Col>
                                <Col xs={6}>
                                    <FormGroup>
                                        <Label>VAT Amount</Label>
                                        <InputGroup>
                                            <InputGroupText>{currencySymbol(currencyValue)}</InputGroupText>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={vatAmount}
                                                onChange={(e) => setVatAmount(e.target.value)}
                                                invalid={attempted && !!errors.vatAmount}
                                            />
                                            <FormFeedback>{errors.vatAmount}</FormFeedback>
                                        </InputGroup>
                                        {lowConfFeedback('vatAmount')}
                                    </FormGroup>
                                </Col>
                            </Row>
                            </fieldset>
                        </Col>
                    </Row>
                    {invoice?.id && (
                        <div className="mt-3">
                            <AccountingSyncBadge type="Invoice" id={invoice.id} onStatusChange={(s) => setLocked(s?.status === 'locked')} />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={handleClose}
                            disabled={updateInvoice.isPending || dupChecking}>
                        Cancel
                    </Button>
                    {!locked && (dupMatches.length > 0 ? (
                        <Button color="warning" type="button" onClick={doSave} disabled={updateInvoice.isPending}>
                            {updateInvoice.isPending && <Spinner size="sm" className="me-1" />}
                            Save anyway
                        </Button>
                    ) : (
                        <Button color="primary" type="submit" disabled={updateInvoice.isPending || dupChecking}>
                            {(updateInvoice.isPending || dupChecking) && <Spinner size="sm" className="me-1" />}
                            Save changes
                        </Button>
                    ))}
                </ModalFooter>
            </Form>
        </Modal>
    );
}
